import React, { useMemo, useState } from "react";
import { CircleStackIcon } from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import { Button } from "../Button";
import { RegisterModal } from "../dialogs/RegisterModal";

const databaseOptions = [
  {
    id: "postgresql",
    name: "PostgreSQL",
    imagePath: "/assets/database_icons/postgres.png",
  },
  {
    id: "mysql",
    name: "MySQL / PlanetScale",
    imagePath: "/assets/database_icons/mysql.png",
  },
  {
    id: "supabase",
    name: "Supabase",
    imagePath: "/assets/database_icons/supabase.png",
  },
  {
    id: "firebase",
    name: "Firebase",
    imagePath: "/assets/database_icons/firebase.png",
  },
  {
    id: "mongodb",
    name: "MongoDB Atlas",
    imagePath: "/assets/database_icons/mongo.png",
  },
  {
    id: "snowflake",
    name: "Snowflake",
    imagePath: "/assets/database_icons/snowflake.png",
  },
  { id: "other", name: "Other" },
];

// Database Details Panel Component
export const DatabaseDetailsPanel = ({
  isOpen,
  onClose,
  isOnboarding = false,
}) => {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedDatasource, setSelectedDatasource] = useState(null);

  const selectedMetadata = useMemo(() => {
    if (!selectedDatasource) return null;
    return {
      datasource: {
        selectedOption: selectedDatasource,
      },
    };
  }, [selectedDatasource]);

  const openWaitlist = (option) => {
    setSelectedDatasource(option);
    setWaitlistOpen(true);
  };

  const closeWaitlist = () => {
    setWaitlistOpen(false);
  };

  // During onboarding, show RegisterModal instead
  if (isOnboarding && isOpen) {
    return (
      <RegisterModal isOpen={isOpen} onClose={onClose} isOnboarding={true} />
    );
  }

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-20">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-xl transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
              >
                <div className="relative flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                  <div className="flex items-start justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <CircleStackIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <DialogTitle className="text-xl font-semibold text-gray-900">
                          Add Data Source
                        </DialogTitle>
                        <p className="text-sm text-gray-500">
                          Choose your preferred datasource method to join the
                          early access list.
                        </p>
                      </div>
                    </div>
                    <Button onClick={onClose} variant="outline">
                      Close
                    </Button>
                  </div>

                  <div className="flex-1 p-6 space-y-6">
                    <section className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Choose your datasource
                      </h3>
                      <div className="space-y-3">
                        {databaseOptions.map((option) => (
                          <Button
                            key={option.id}
                            variant="solid"
                            color="primary"
                            className="w-full justify-center gap-3"
                            onClick={() => openWaitlist(option)}
                          >
                            {option.imagePath && (
                              <img
                                src={option.imagePath}
                                alt={option.name}
                                className="w-6 h-6 object-contain"
                              />
                            )}
                            <span>{option.name}</span>
                          </Button>
                        ))}
                      </div>
                    </section>

                    <div className="border-t border-gray-100 pt-4">
                      <Button
                        variant="outline"
                        color="slate"
                        className="w-full justify-center"
                        onClick={() =>
                          openWaitlist({
                            id: "connection-string",
                            name: "Connection string",
                          })
                        }
                      >
                        Connect using connection string
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>

      <RegisterModal
        isOpen={waitlistOpen}
        onClose={closeWaitlist}
        selectedMetadata={selectedMetadata}
      />
    </>
  );
};
